import React from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Space,
    Drawer,
    Pagination
} from "antd";
import { CloseOutlined, ExportOutlined } from "@ant-design/icons";
import { DRAWER_PLACEMENTS } from "../../../../config/constants";
import Datatable from "../../../shared/components/Datatable";
import styles from  "./TableDrawer.module.css"

interface TableDrawerProps {
    title: string
    openDrawer: boolean,
    columns: any,
    tableData: any,
    setTableData: React.Dispatch<any>,
    isTableDataLoading: boolean,
    tablePageIndex: number,
    setTablePageIndex: React.Dispatch<number>,
    tablePageSize: number,
    setTablePageSize: React.Dispatch<number>,
    onClose: () => void,
    scroll?: any,
    onExport?: () => void,
}

const TableDrawer = ({
    title,
    openDrawer,
    columns,
    tableData,
    setTableData,
    isTableDataLoading,
    tablePageIndex,
    setTablePageIndex,
    tablePageSize,
    setTablePageSize,
    onClose,
    scroll = undefined,
    onExport = undefined,
}: TableDrawerProps) => {
    const { t, i18n } = useTranslation("translations");

    return (
        <Drawer
            height={"95vh"}
            width={"400px"}
            getContainer={false}
            className={styles.drawerTable}
            title={title}
            placement={DRAWER_PLACEMENTS.BOTTOM}
            onClose={onClose}
            visible={openDrawer}
            closable={false}
            extra={
                <Space>
                    <>
                        {onExport && (<Button
                            icon={<ExportOutlined />}
                            size="small"
                            onClick={onExport}
                        >
                            {t("General.Labels.Export", {
                                lngs: i18n.languages,
                                defaultValue: "Export",
                            })}
                        </Button>)}
                        <Button
                            type="ghost"
                            onClick={onClose}
                            icon={<CloseOutlined />}
                            size="small"
                        />
                    </>
                </Space>
            }
        >
            <div className={styles.drawerTableUtility}>
                <div className={styles.drawerTableFilter}>
                    {/* Empty in case filtering is needed at a later stage. */}
                </div>

                <Pagination
                    size="small"
                    total={tableData.length}
                    current={tablePageIndex}
                    pageSize={tablePageSize}
                    showQuickJumper={true}
                    showSizeChanger={true}
                    locale={{
                        items_per_page: t("General.Table.Pagination.ItemPerPage", {
                            lngs: i18n.languages,
                            defaultValue: "/page",
                        })!,
                        page: t("General.Table.Pagination.Page", {
                            lngs: i18n.languages,
                            defaultValue: "page",
                        })!,
                        jump_to: t("General.Table.Pagination.GoTo", {
                            lngs: i18n.languages,
                            defaultValue: "Go to",
                        })!,
                    }}
                    showTotal={(total: number, range: [number, number]) => {
                        const translation = t("General.Table.Pagination.RangeRecords", {
                            lngs: i18n.languages,
                            defaultValue: `${range[0]}-${range[1]} out of ${total} records`,
                        });
                        return `${translation
                            // eslint-disable-next-line no-template-curly-in-string
                            .replace("${range[0]}", range[0].toString())
                            // eslint-disable-next-line no-template-curly-in-string
                            .replace("${range[1]}", range[1].toString())
                            // eslint-disable-next-line no-template-curly-in-string
                            .replace("${total}", total.toString())}`;
                    }}
                    itemRender={(_page, _type, originalElement) => {
                        return originalElement;
                    }}
                    onChange={(page, size) => {
                        setTablePageIndex(page);
                        setTablePageSize(size);
                    }}
                />
            </div>
            <div>
                <Datatable
                    scroll={scroll}
                    tableColumns={columns}
                    data={tableData}
                    setData={setTableData}
                    isTableLoading={isTableDataLoading}
                    paginationVisible={false}
                    paginationPageIndex={tablePageIndex}
                    paginationPageSize={tablePageSize}
                    showActionsColumn={false}
                    checkPermissions={true}
                ></Datatable>
            </div>
        </Drawer>
    )
};

export default TableDrawer;
